import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * ResponsiveTabGroup Component
 * 
 * Adaptive tab navigation that shows visible tabs inline and overflow tabs in a dropdown.
 * Supports horizontal scrolling on mobile with swipe gestures.
 * Part of the Base44 Design System.
 * 
 * @param {Array} tabs - Array of tab objects with { id, label, badge }
 * @param {string} activeTab - Currently active tab id
 * @param {function} onTabChange - Tab change handler
 * @param {string} className - Additional classes for container
 * @param {number} maxWidth - Maximum width in pixels for the tab container
 */
export const ResponsiveTabGroup = ({ 
  tabs = [], 
  activeTab, 
  onTabChange, 
  className,
  maxWidth = 800
}) => {
  const containerRef = useRef(null);
  const activeTabRef = useRef(null);

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (activeTabRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeTabEl = activeTabRef.current;
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTabEl.getBoundingClientRect();
      
      // Check if tab is outside visible area
      if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
        activeTabEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab]);

  const renderTabButton = (tab) => (
    <button
      key={tab.id}
      ref={tab.id === activeTab ? activeTabRef : null}
      onClick={() => onTabChange(tab.id)}
      className={cn(
        "rounded-full px-4 md:px-6 py-2 text-xs md:text-sm font-medium transition-all whitespace-nowrap relative flex-shrink-0",
        tab.id === activeTab
          ? "bg-white dark:bg-slate-700 text-primary shadow-sm"
          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
      )}
    >
      {tab.label}
      {tab.badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md">
          {tab.badge > 99 ? '99+' : tab.badge}
        </span>
      )}
    </button>
  );

  return (
    <div 
      ref={containerRef}
      className={cn(
        // Container com estilo pill
        "bg-slate-100 dark:bg-slate-800 rounded-full p-1",
        // Overflow e scroll horizontal
        "overflow-x-auto",
        // Esconder scrollbar
        "scrollbar-hide",
        // Largura máxima
        "max-w-full",
        className
      )}
      style={{ maxWidth: `${maxWidth}px` }}
    >
      <div className="inline-flex gap-1 min-w-max">
        {tabs.map(renderTabButton)}
      </div>
    </div>
  );
};
